import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BlockButtonEditorPopover } from '@/components/shared/button-editor-popover';
import { type BlockType, getBlockUiKey, type PageBlock } from '@/types/block';
import { useBlockSelectionActions } from '../block-selection';
import { EmbedBlockSettingsModal } from './embed-block-settings-modal';
import { FormBlockEditorPopover } from './form-block-editor-popover';
import { ImageBlockSettingsModal } from './image-block-selector-modal';
import { RawHtmlBlockSettingsModal } from './raw-html-block-settings-modal';
import { VideoBlockSettingsModal } from './video-block-settings-modal';

export function useLeafBlockEditOverlays(
  sectionId: string,
  editorSectionId: string,
  block: PageBlock,
  anchorRef?: RefObject<HTMLElement | null>,
  parentBlockType?: BlockType | null,
) {
  const showFloatOption = parentBlockType === 'group';
  const { beginInlineEdit, setInlineEdit } = useBlockSelectionActions();
  const blockUiKey = getBlockUiKey(block);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const overlayOwnerKey = `${editorSectionId}:${blockUiKey}`;
  const previousOverlayOwnerKeyRef = useRef(overlayOwnerKey);

  useEffect(() => {
    if (previousOverlayOwnerKeyRef.current === overlayOwnerKey) return;
    previousOverlayOwnerKeyRef.current = overlayOwnerKey;
    setOverlayOpen(false);
  }, [overlayOwnerKey]);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
    setInlineEdit(editorSectionId, null);
  }, [editorSectionId, setInlineEdit]);

  const handlePrimaryEdit = useCallback(() => {
    if (overlayOpen) {
      setOverlayOpen(false);
      setInlineEdit(editorSectionId, null);
      return;
    }
    setOverlayOpen(true);
    beginInlineEdit(editorSectionId, blockUiKey, { suppressPageSectionChrome: false });
  }, [beginInlineEdit, blockUiKey, editorSectionId, overlayOpen, setInlineEdit]);

  const handleOverlayOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        closeOverlay();
        return;
      }

      setOverlayOpen(true);
    },
    [closeOverlay],
  );

  let onPrimaryEdit: (() => void) | undefined;
  let overlayNode: ReactNode = null;

  switch (block.blockType) {
    case 'image': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <ImageBlockSettingsModal
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          onOpenChange={handleOverlayOpenChange}
          anchorRef={anchorRef}
          showFloatOption={showFloatOption}
        />
      );
      break;
    }
    case 'video': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <VideoBlockSettingsModal
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          onOpenChange={handleOverlayOpenChange}
          anchorRef={anchorRef}
          showFloatOption={showFloatOption}
        />
      );
      break;
    }
    case 'button': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <BlockButtonEditorPopover
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          anchorRef={anchorRef}
          onOpenChange={handleOverlayOpenChange}
        />
      );
      break;
    }
    case 'embed': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <EmbedBlockSettingsModal
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          onOpenChange={handleOverlayOpenChange}
          anchorRef={anchorRef}
          showFloatOption={showFloatOption}
        />
      );
      break;
    }
    case 'raw_html': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <RawHtmlBlockSettingsModal
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          onOpenChange={handleOverlayOpenChange}
          anchorRef={anchorRef}
        />
      );
      break;
    }
    case 'form': {
      onPrimaryEdit = handlePrimaryEdit;
      overlayNode = (
        <FormBlockEditorPopover
          sectionId={sectionId}
          block={block}
          open={overlayOpen}
          anchorRef={anchorRef}
          onOpenChange={handleOverlayOpenChange}
          showFloatOption={showFloatOption}
        />
      );
      break;
    }
    default: {
      onPrimaryEdit = undefined;
      overlayNode = null;
    }
  }

  return { onPrimaryEdit, overlayNode };
}
